import ApplySkeleton from '@core/components/ApplyDialogV2/ApplySkeleton'
import ApplyV2Button from '@core/components/ApplyDialogV2/ApplyV2Button'
import useApplyV2NavTranslations from '@core/utils/hooks/useApplyV2NavTranslations'
import React, { useEffect, useState } from 'react'
import { useApplyV2Context } from '@core/utils/contexts/ApplyV2ModalContext/ApplyV2ModalContext'
import {
    useGetSkillsV2ApplyQuery,
    usePostSkillV2ApplyMutation,
    useDeleteSkillV2ApplyMutation,
} from '@core/app/api/applyJobV2Api'
import type { ApplyV2TempSkill } from '@core/app/types/applyV2Type'
import './ApplyV2SkillsStep.scss'
import { useTranslation } from 'react-i18next'

interface SkillItem {
    id: number
    name: string
    uid?: string
}

const baseClass = 'ApplyV2SkillsStep'

const SkillsStep: React.FC = () => {
    const { t } = useTranslation()
    const { getTitle } = useApplyV2NavTranslations()
    const { postProfileData, onNextStep, postProfileRefetch, isPostProfileLoading, isPostApplied } = useApplyV2Context()
    const uid = postProfileData?.data?.uid ?? ''
    const [inputValue, setInputValue] = useState('')
    const [selectedSkills, setSelectedSkills] = useState<SkillItem[]>([])

    const { data, isLoading: isSkillsLoading } = useGetSkillsV2ApplyQuery(
        { query: inputValue },
        { skip: inputValue.length < 2 }
    )
    const [addSkill, { isLoading: isAddLoading }] = usePostSkillV2ApplyMutation()
    const [deleteSkill, { isLoading: isDeleteLoading }] = useDeleteSkillV2ApplyMutation()

    const suggestions: { id: number; name: string }[] =
        data?.data?.map((s: any) => ({
            id: s.id,
            name: s.name,
        })) ?? []

    const handleAddSkill = async (name: string, skillTagId?: number) => {
        if (!uid) return
        const exists = selectedSkills.find((s) => s.name.toLowerCase() === name.toLowerCase())
        if (exists) return

        const skill = {
            uid,
            body: {
                skill: name,
                skillTagId: skillTagId ?? 0,
                rating: 0,
            },
        }

        try {
            setInputValue('')
            const response = await addSkill(skill).unwrap()
            const skillUid = response?.data?.skill?.uid ?? undefined

            setSelectedSkills((prev) => [
                ...prev,
                {
                    id: skillTagId ?? Date.now(),
                    name,
                    uid: skillUid,
                },
            ])

            // setInputValue('')
        } catch (err) {
            console.error(err)
        }
    }

    const handleRemoveSkill = async (skill: SkillItem) => {
        if (!uid || !skill.uid) return
        try {
            setSelectedSkills((prev) => prev.filter((s) => s.name !== skill.name))
            await deleteSkill({ uid: postProfileData?.data?.uid, skillUid: skill.uid })
        } catch (err) {
            console.error(err)
        }
    }

    const handleKeyDown = (e: React.KeyboardEvent<HTMLInputElement>) => {
        if (e.key === 'Enter' && inputValue.trim() !== '') {
            e.preventDefault()
            handleAddSkill(inputValue.trim())
        }
    }

    useEffect(() => {
        if (!postProfileData?.data?.skill) return

        const initialSkills: SkillItem[] = postProfileData.data.skill
            .filter((s) => s.skill)
            .map((s: ApplyV2TempSkill) => ({
                id: s.skillTagId ?? Date.now(),
                name: s.skill!,
                uid: s.uid,
            }))

        setSelectedSkills(initialSkills)
    }, [postProfileData?.data?.skill])

    console.log(selectedSkills)

    return (
        <div className="SkillStep">
            <div className="SkillStep__input-wrapper">
                <div className="SkillStep__skill-title">{t('3294', 'Add skill')}</div>
                <input
                    className="SkillStep__input"
                    placeholder="Type a skill"
                    value={inputValue}
                    onChange={(e) => setInputValue(e.target.value)}
                    onKeyDown={handleKeyDown}
                />
                {inputValue && (
                    <div className="SkillStep__dropdown">
                        <div
                            className="SkillStep__option SkillStep__option--add"
                            onClick={() => handleAddSkill(inputValue.trim())}
                        >
                            {t('179', 'Add')} “{inputValue}”
                        </div>
                        {suggestions.length > 0 && (
                            <>
                                <div className="SkillStep__option SkillStep__option--label">
                                    {t('7310', 'Suggestions')}
                                </div>
                                {suggestions.map((s) => (
                                    <div
                                        key={s.id}
                                        className="SkillStep__option"
                                        onClick={() => handleAddSkill(s.name, s.id)}
                                    >
                                        {s.name}
                                    </div>
                                ))}
                            </>
                        )}
                    </div>
                )}
            </div>

            <div className="SkillStep__tags">
                {selectedSkills.map((skill) => (
                    <div key={skill.name} className="SkillStep__tag">
                        {skill.name}
                        <span className="SkillStep__tag-remove" onClick={() => handleRemoveSkill(skill)}>
                            ✕
                        </span>
                    </div>
                ))}
                {isAddLoading && (
                    <ApplySkeleton isLoading={true}>
                        <div className="SkillStep__tag">ddd</div>
                    </ApplySkeleton>
                )}
            </div>
            <div className="row right">
                {/*<button type="button" className="next-btn" disabled={selectedSkills.length === 0} onClick={onNextStep}>*/}
                {/*    {t('3035', 'Next')}*/}
                {/*</button>*/}
                <ApplyV2Button
                    variant={'primary'}
                    className={`${baseClass}__next--btn`}
                    onClick={onNextStep}
                    isLoading={isPostApplied || isPostProfileLoading}
                    // isDisabled={isPostApplied || isPostProfileLoading || (!isNoItems && references.length === 0)}
                    isDisabled={isPostApplied || isPostProfileLoading}
                    type={'submit'}
                >
                    {getTitle('3035', 'Next')}
                </ApplyV2Button>
            </div>
        </div>
    )
}

export default SkillsStep
